import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  ThunkAction,
  AnyAction,
} from "@reduxjs/toolkit";
import {
  addAuthorizationBearerToken,
  removeAuthorizationBearerToken,
} from "../../api/punctApi";
import { RootState, Thunk } from "../../redux";
import { UserRole } from "../../generated/graphql";
import { authSerializer } from "./utils/authSerializer";
import { refreshCredentials } from "../auth0/functions/refreshCredentials";

export interface AuthState {
  email?: string;
  userId?: string;
  accessToken?: string;
  companyName?: string;
  name?: string;
  userImage?: string;
  roles?: UserRole[];
  companyId?: number;
  refreshToken?: string;
  expire?: number;
  expiresAt?: number;
}

export interface SuccessfulAuth extends AuthState {
  userId: string;
  accessToken: string;
  refreshToken: string;
}

interface AuthSliceState extends AuthState {
  // False until refreshSavedCredentials (dispatched once from _app.tsx's
  // useEffect) actually resolves - whether it finds a valid session or not.
  // AuthGuard/useAuthGuard must wait for this before deciding whether to
  // redirect: effects fire children-first, so AuthGuard's own effect (a
  // descendant of _app.tsx) always runs before _app.tsx's, and would
  // otherwise conclude "no roles yet" and redirect away before the saved
  // credentials ever get a chance to load - a state update from the
  // dispatch itself doesn't have that problem (it triggers a fresh render
  // for every consumer, regardless of original effect order), only the
  // guard's own redirect-on-mount logic does.
  isInitialized: boolean;
}

// Deliberately NOT authSerializer.load() here: that reads localStorage,
// unavailable server-side, so an already-logged-in user's client-side
// initialState would differ from what the server rendered - every
// component reading auth state (AuthGuard, header nav, HeaderAvatar, ...)
// would then hydrate mismatched under React 18. store.ts's dispatch of
// refreshSavedCredentials() already loads the same saved credentials right
// after the store is created - that's a real async action (resolves on a
// microtask, after the first render), not a hydration-time state read, so
// it updates safely post-mount instead of baking the mismatch in from the
// start.
const initialState: AuthSliceState = { isInitialized: false };

const {
  actions: { resetAuthSuccess, updateAuthSuccess },
  reducer: authReducer,
} = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetAuthSuccess: () => ({ isInitialized: true }),
    updateAuthSuccess: (state, action: PayloadAction<AuthState>) => ({
      ...action.payload,
      isInitialized: true,
    }),
  },
  extraReducers(builder) {
    builder.addCase(refreshSavedCredentials.fulfilled, (state, action) => {
      // action.payload is undefined on the path where the thunk instead
      // delegates to refreshCredentialsAction (which resolves its own
      // credentials via a separate, already-dispatched updateAuthSuccess
      // rather than returning them) - spreading state first, rather than
      // replacing it outright, preserves whatever that nested dispatch
      // already set instead of clobbering it back to just
      // { isInitialized: true }.
      return { ...state, ...action.payload, isInitialized: true };
    });
  },
});

const updateAuth =
  (state: SuccessfulAuth): Thunk<void> =>
  async (dispatch) => {
    try {
      const { accessToken } = state;

      authSerializer.save(state);

      addAuthorizationBearerToken(accessToken);
      dispatch(updateAuthSuccess(state));
    } catch (_err) {
      dispatch(resetAuthSuccess());
    }
  };

const resetAuth = (): Thunk<void> => (dispatch) => {
  authSerializer.reset();
  removeAuthorizationBearerToken();
  dispatch(resetAuthSuccess());
};

let refreshLock = false;

const refreshCredentialsAction =
  (state: SuccessfulAuth): Thunk<void> =>
  async (dispatch) => {
    try {
      if (refreshLock) {
        return;
      }
      refreshLock = true;
      const newCredentials = await refreshCredentials(state.refreshToken);
      if (!newCredentials?.accessToken) {
        refreshLock = false;
        return dispatch(resetAuth());
      }
      dispatch(updateAuth(newCredentials));

      // refresh the new credentials again before they expire
      if (newCredentials.expire) {
        setTimeout(
          () => dispatch(refreshCredentialsAction(newCredentials)),
          (newCredentials.expire - 10) * 1000,
        );
      }

      refreshLock = false;
    } catch (err) {
      console.error(err);
      refreshLock = false;
    }
  };

// async thunk that checks the local storage for saved credentials and refreshes them
// this is used in order to not ask for credentials every time the app loads
const refreshSavedCredentials = createAsyncThunk(
  "auth/refreshSavedCredentials",
  async (_, { dispatch }) => {
    try {
      const initialState = authSerializer.load();
      if (!initialState || !initialState.refreshToken) {
        return {};
      }

      // Refresh tokens are single-use/rotating - Auth0 invalidates the old
      // one on every exchange. This thunk runs on every fresh page load
      // (dispatched once in store.ts), so unconditionally refreshing here
      // meant rapid successive page loads (e.g. a test suite's cy.visit()
      // sequence, or just a user clicking around normally) could race to
      // exchange the same soon-to-be-stale token and get "invalid_grant"
      // rejected by Auth0, logging the user out. If the cached access
      // token isn't actually expired yet, there's no need to touch the
      // network at all - just use it, and let refreshCredentialsAction's
      // own setTimeout (still scheduled below) refresh it once it's
      // actually close to expiring.
      if (initialState.expiresAt && initialState.expiresAt > Date.now()) {
        if (initialState.expire) {
          setTimeout(
            () =>
              dispatch(
                refreshCredentialsAction(
                  initialState,
                ) as unknown as ThunkAction<void, unknown, unknown, AnyAction>,
              ),
            initialState.expiresAt - Date.now() - 10_000,
          );
        }
        return initialState;
      }

      return dispatch(
        refreshCredentialsAction(initialState) as unknown as ThunkAction<
          void,
          unknown,
          unknown,
          AnyAction
        >,
      );
    } catch (_err) {
      dispatch(resetAuthSuccess());
    }
  },
);

const getAuthState =
  () =>
  ({ auth }: RootState): AuthState => ({
    userId: auth.userId,
    email: auth.email,
    accessToken: auth.accessToken,
    name: auth.name,
    companyName: auth.companyName,
    userImage: auth.userImage,
    roles: auth.roles,
    companyId: auth.companyId,
    refreshToken: auth.refreshToken,
    expire: auth.expire,
    expiresAt: auth.expiresAt,
  });

const getAccessToken = ({ auth }: RootState): string | undefined =>
  auth.accessToken;

const getUserRoles = ({ auth }: RootState): UserRole[] => auth.roles || [];
const getCompanyId = ({ auth }: RootState) => auth.companyId;
const getIsAuthInitialized = ({ auth }: RootState): boolean =>
  (auth as AuthSliceState).isInitialized;

export {
  authReducer,
  getAccessToken,
  getAuthState,
  getUserRoles,
  getCompanyId,
  getIsAuthInitialized,
  updateAuth,
  resetAuth,
  refreshCredentialsAction,
  refreshSavedCredentials,
};
